/**
 * Monthly / Yearly switch. Yearly is the default everywhere (config:
 * DEFAULT_BILLING_INTERVAL) and is never called "Annually". Radio-group
 * semantics: Tab focuses the checked option, arrow keys move the choice.
 */
import { useRef } from 'react'
import type { KeyboardEvent } from 'react'
import { useTranslation } from '../../lib/i18n'
import { yearlySavingsPercent } from '../../lib/pricing'
import type { BillingInterval } from '../../lib/pricing'

const OPTIONS: BillingInterval[] = ['year', 'month']

export function BillingToggle({
  value,
  onChange,
}: {
  value: BillingInterval
  onChange: (value: BillingInterval) => void
}) {
  const { t } = useTranslation()
  const refs = useRef<Array<HTMLButtonElement | null>>([])

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
      return
    }
    event.preventDefault()
    const next = value === 'year' ? 'month' : 'year'
    onChange(next)
    refs.current[OPTIONS.indexOf(next)]?.focus()
  }

  return (
    <div
      className="billing-toggle"
      role="radiogroup"
      aria-label={t('pricing.toggleLabel')}
      data-value={value}
      onKeyDown={onKeyDown}
    >
      <span className="billing-toggle-thumb" aria-hidden="true" />
      {OPTIONS.map((option, index) => (
        <button
          key={option}
          ref={(element) => {
            refs.current[index] = element
          }}
          type="button"
          role="radio"
          aria-checked={value === option}
          tabIndex={value === option ? 0 : -1}
          className={value === option ? 'is-active' : undefined}
          onClick={() => onChange(option)}
        >
          {option === 'year' ? t('pricing.yearly') : t('pricing.monthly')}
          {option === 'year' ? (
            <span className="billing-toggle-save">
              {t('pricing.savePercent', {
                percent: yearlySavingsPercent('pro'),
              })}
            </span>
          ) : null}
        </button>
      ))}
    </div>
  )
}
